import type { JSX } from 'react'
import type React from 'react'
import type { Vendor, Service } from '@type/vendors'
import type { WorkflowPrivacy } from '@type/orgstructure'
import type {
  ApprovalRule,
  CommunicationType,
  CompletionType,
  Frequency,
  RejectionRule,
  WorkflowType,
  EmailRequestsAvailability,
  StepFlow,
} from '@type/common'
import { FieldType, IntegrationType, FormType } from '@type/common'
import type {
  FieldSource,
  GroupUse,
  Workflow,
  WorkflowDecisionHelperMode,
  WorkflowFieldBindings,
} from '@models/Workflow'
import type { GranteeFormConfig } from '@models/Pipelines'
import type { NetsuiteSetting } from './types'
import type { Integration as IntegrationVendor } from '@type/integration'
import type { Feature } from '@type/features'

export { FormType }

export { FieldType }

export const enum FieldGroup {
  general = 'general',
  finance = 'finance',
  softledger = 'softledger',
  netsuite = 'netsuite',
  xero = 'xero',
  quickbooks = 'quickbooks',
  campfire = 'campfire',
}

export type FieldBase = {
  Icon: (props: React.ComponentProps<'svg'>) => JSX.Element
  label: string
  tooltip?: string
  tip?: string
  group?: FieldGroup
  feature?: Feature
}

export type CommonInfo = {
  id?: number
  name: string
  channel: CommunicationType
  slackChannel?: string
  category: Array<number>
  privacy: WorkflowPrivacy

  watchers?: Array<string | number>

  description?: string | null
  formType: FormType | null
  workflowType: WorkflowType

  groups?: { [groupId: string]: FormGroup }

  template?: number

  emailRequestsAvailability: EmailRequestsAvailability
  slug?: string

  publicForm: PublicForm

  organization?: number
  integration?: IntegrationVendor
  apiEnabled: boolean

  approversRequired: boolean
}

export type Template = number | null

export type JiraIntegrationSettings = {
  project?: {
    id: string
    name: string
  }
  issue?: {
    id: string
    name: string
  } | null
  assignee?: {
    accountId: string
    name: string
  } | null
}
export type EmailConditionOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains'
export type EmailConditionLogic = 'and' | 'or'

export interface EmailConditionClause {
  id: string
  fieldId: string
  operator: EmailConditionOperator
  value: string
}

export interface EmailCondition {
  id: string
  logic: EmailConditionLogic
  clauses: EmailConditionClause[]
  emails: string[]
}

export type EmailIntegrationSettings = {
  emails?: string[]
  fields?: string[]
  statuses?: string[]
  description?: string | null
  sendEmailConfirmation?: boolean
  emailTemplate?: string
  emailSubject?: string
  emailConditions?: EmailCondition[]
}

export type ApiHeader = Record<string, unknown>
export type ApiIntegrationSettings = {
  fields: string[]
  statuses: string[]
  headers: ApiHeader
  url: string | null
  urls: string[]
  enable_retries?: boolean
  hmac_secret?: string
}

export type Integration = {
  vendor: Vendor
  service?: Service
  key?: string
  jira?: JiraIntegrationSettings | null
  email?: EmailIntegrationSettings | null
  api?: ApiIntegrationSettings | null
  netsuiteSetting?: NetsuiteSetting | null
}

export type IntegrationState = {
  inbound: Integration | null
  outbound: Integration[]
}

export interface FormGroup {
  id: string
  name: string
  order: number
  use?: GroupUse
  collapsed?: boolean
}

// Mutually exclusive with inline `options`. When set on a dropdown /
// multi-select, the field renders options fetched from the referenced
// CustomList; submissions snapshot the picked label into readable_value
// so admin-side renames don't rewrite historical requests.
//
// Re-exported from the API model rather than redeclared: the builder's
// FormField and the wire-format WorkflowField carry the same value, and two
// structurally-identical declarations drift the moment one gains a field.
export type { FieldSource }

export type FormField = {
  label: string
  customName?: string
  options?: string[]
  source?: FieldSource
  columns?: FormState
  type: FieldType
  id: string
  templateFieldId?: string | null
  order: number
  required: boolean
  hideInSlack: boolean
  binding?: WorkflowFieldBindings
  reqiredByDefault?: boolean
  conditions?: Conditions
  groupId?: string // Optional group assignment
}

// Stage config for the external grantee form. Everything the feature needs is
// named here rather than flagged on individual fields.
// One definition of the grantee payload, in the model layer, aliased here so
// builder code can keep referring to it by the name it uses. Declaring a second
// structural type over the same wire shape is what let the two drift.
export type { GranteeSendWhen } from '@models/Pipelines'
export type GranteeForm = GranteeFormConfig

export type FormState = {
  [key: string]: FormField
}

export interface PublicForm {
  uuid: string | null
  isAuthorizedOnly: boolean | null
}

export interface DataSource {
  form: FormState
  workflowType: WorkflowType
  formType: FormType | null
  publicForm: PublicForm
  isPannelCollapsed: boolean
  formDescription?: string | null
  apiEnabled?: boolean
  lineItemsEditId?: string
  isAddingFieldWhenCollapsed?: string
  editableFieldId?: string | null
  groups?: { [groupId: string]: FormGroup } // Optional groups structure
}

export const enum ApprovalStepSubject {
  task = 'task',
  step = 'step',
}

export const enum ApprovalStepAction {
  approve = 'approve',
  reject = 'reject',
}

export interface Condition {
  rule?: Rule
  value?: number[] | string | string[] | Range
  field: string
  useCurrencyRate?: boolean
}

export interface Conditions {
  [key: string]: Condition
}

export interface Approver {
  id: number
  name: string
  roles: string[]
  channel: CommunicationType[]
}

export interface ApprovalStep {
  id: string
  approvers: Approver[]
  completionType: CompletionType
  approvalRule?: ApprovalRule | null
  rejectionRule?: RejectionRule | null
  type: StepFlow
  order: number
  department?: string
  role?: string[]
  name: string
  description?: string
  subject: ApprovalStepSubject
  actions: {
    [ApprovalStepAction.approve]: string
    [ApprovalStepAction.reject]: string
  }
  customApprovers: CustomApprover[]
  conditionsEnabled?: boolean
  defaultCondition: boolean
  conditions: Conditions
}

export interface ApprovalStepsState {
  [key: string]: ApprovalStep
}

export interface Range {
  from: string
  to: string
}

export const enum Rule {
  Less = 'Less',
  Range = 'Range',
  Greater = 'Greater',
  Contains = 'Contains',
}

export type Reminder = {
  frequency?: Frequency
  time?: string
  timezone?: string
}

export interface ApprovalFlowState {
  steps: ApprovalStepsState
  reminder?: Reminder
  reminderEnabled: boolean
  watchers?: Array<string | number>
  watchersEnabled: boolean
  draftEnabled: boolean
  draftEditors?: Array<string | number>
  decisionHelperEnabled: boolean
  decisionHelperMode: WorkflowDecisionHelperMode | null
  decisionHelperModerators?: Array<number>
  slackChannel?: string
  slackChannelEnabled?: boolean
  notifyAll?: boolean
  excludeRequestorFromApprovers?: boolean
  excludeDuplicateApprovers?: boolean
  selectedStep?: string
  showStepInEmail?: boolean
  signature21CFRPart11Enabled?: boolean
}

export type Value = string | boolean | number | (string | number)[] | undefined

export const enum CustomApprover {
  specifiedByRequestor = 'specified_by_requestor',
  requestor = 'requestor',
  manager = 'manager',
  managerOfManager = 'manager_manager',
  costCenterOwner = 'cost_center_owner',
}

export type GroupedConditions = { [key: string]: { conditions?: Conditions; steps: ApprovalStepsState } }

export type Draft = {
  commonInfo: CommonInfo
  form: FormState
  approvalFlow: ApprovalFlowState
  integration: IntegrationState
  uuid: string
  index?: number
}

export interface ApprovalForm {
  intakeForm: FormState
  approvalFlow: ApprovalFlowState
  commonInfo: CommonInfo
  integration: IntegrationState
  workflow?: Workflow<IsResponse>
}
